import React, { useState, useEffect } from 'react';
import { ref, onValue, push, update, remove } from 'firebase/database';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../../firebase/config';
import { FaGift, FaPlus, FaEdit, FaTrash, FaToggleOn, FaToggleOff, FaSearch, FaTimes, FaImage, FaUpload, FaTag } from 'react-icons/fa';

const OfferProducts = () => {
  const [offerProducts, setOfferProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [uploading, setUploading] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    label: '',
    originalPrice: '',
    offerPrice: '',
    discount: '',
    imageUrl: '',
    stock: '',
    active: true
  });

  const labels = ['Hot Deal', 'Limited Stock', 'Best Seller', 'New Arrival', 'Clearance', 'Flash Sale'];

  useEffect(() => {
    const offerProductsRef = ref(db, 'HTAMS/company/offerproduct');
    const unsubscribe = onValue(offerProductsRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const productsArray = Object.entries(data).map(([id, val]) => ({
          id,
          ...val
        })).sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
        setOfferProducts(productsArray);
      } else {
        setOfferProducts([]);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const calculateDiscount = (original, offer) => {
    if (!original || !offer) return 0;
    return Math.round(((original - offer) / original) * 100);
  };

  // Resize image before upload
  const resizeImage = (file) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const MAX_WIDTH = 800;
          const MAX_HEIGHT = 800;
          let width = img.width;
          let height = img.height;

          if (width > height) {
            if (width > MAX_WIDTH) {
              height *= MAX_WIDTH / width;
              width = MAX_WIDTH;
            }
          } else {
            if (height > MAX_HEIGHT) {
              width *= MAX_HEIGHT / height;
              height = MAX_HEIGHT;
            }
          }

          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);

          canvas.toBlob((blob) => {
            resolve(blob);
          }, 'image/jpeg', 0.8);
        };
        img.src = e.target.result;
      };
      reader.readAsDataURL(file);
    });
  };

  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const uploadImage = async () => {
    if (!imageFile) return formData.imageUrl;

    try {
      setUploading(true);
      // Resize image
      const resizedBlob = await resizeImage(imageFile);

      // Upload to Firebase Storage
      const timestamp = Date.now();
      const fileName = `offerproducts/${timestamp}_${imageFile.name}`;
      const imageRef = storageRef(storage, fileName);

      await uploadBytes(imageRef, resizedBlob);
      const downloadURL = await getDownloadURL(imageRef);

      setUploading(false);
      return downloadURL;
    } catch (error) {
      console.error('Error uploading image:', error);
      setUploading(false);
      alert('❌ Failed to upload image');
      return formData.imageUrl;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      // Upload image if new file selected
      const imageUrl = await uploadImage();

      const discount = calculateDiscount(
        Number(formData.originalPrice),
        Number(formData.offerPrice)
      );

      const productData = {
        name: formData.name,
        description: formData.description,
        label: formData.label,
        originalPrice: Number(formData.originalPrice),
        offerPrice: Number(formData.offerPrice),
        discount: discount,
        imageUrl: imageUrl,
        stock: Number(formData.stock) || 0,
        active: formData.active,
        updatedAt: Date.now()
      };

      if (editingProduct) {
        await update(ref(db, `HTAMS/company/offerproduct/${editingProduct.id}`), productData);
        alert('✅ Offer product updated successfully!');
      } else {
        productData.createdAt = Date.now();
        await push(ref(db, 'HTAMS/company/offerproduct'), productData);
        alert('✅ Offer product created successfully!');
      }

      resetForm();
    } catch (error) {
      console.error('Error saving offer product:', error);
      alert('❌ Failed to save offer product');
    }
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setFormData({
      name: product.name || '',
      description: product.description || '',
      label: product.label || '',
      originalPrice: product.originalPrice || '',
      offerPrice: product.offerPrice || '',
      discount: product.discount || '',
      imageUrl: product.imageUrl || '',
      stock: product.stock || '',
      active: product.active !== false
    });
    setImagePreview(product.imageUrl || '');
    setShowModal(true);
  };

  const handleDelete = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this offer product?')) return;

    try {
      await remove(ref(db, `HTAMS/company/offerproduct/${productId}`));
      alert('✅ Offer product deleted successfully!');
    } catch (error) {
      console.error('Error deleting offer product:', error);
      alert('❌ Failed to delete offer product');
    }
  };

  const toggleStatus = async (product) => {
    try {
      await update(ref(db, `HTAMS/company/offerproduct/${product.id}`), {
        active: !product.active
      });
    } catch (error) {
      console.error('Error toggling status:', error);
      alert('❌ Failed to update status');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      label: '',
      originalPrice: '',
      offerPrice: '',
      discount: '',
      imageUrl: '',
      stock: '',
      active: true
    });
    setEditingProduct(null);
    setImageFile(null);
    setImagePreview('');
    setShowModal(false);
  };

  const filteredProducts = offerProducts.filter(product =>
    product.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    product.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="offer-products-container">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading offer products...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="offer-products-container">
      {/* Header */}
      <div className="header">
        <div className="header-content">
          <FaGift className="header-icon" />
          <div>
            <h1>Offer Products</h1>
            <p>Manage special offer products</p>
          </div>
        </div>
        <button className="btn-add" onClick={() => setShowModal(true)}>
          <FaPlus /> Add Product
        </button>
      </div>

      {/* Search */}
      <div className="search-section">
        <div className="search-box">
          <FaSearch />
          <input
            type="text"
            placeholder="Search products..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <FaTimes className="clear-btn" onClick={() => setSearchQuery('')} />
          )}
        </div>
      </div>

      {/* Products Grid */}
      <div className="products-grid">
        {filteredProducts.length === 0 ? (
          <div className="empty-state">
            <FaGift className="empty-icon" />
            <p>No offer products yet</p>
            <button className="btn-add" onClick={() => setShowModal(true)}>
              <FaPlus /> Add First Product
            </button>
          </div>
        ) : (
          filteredProducts.map((product) => (
            <div key={product.id} className={`product-card ${!product.active ? 'inactive' : ''}`}>
              {/* Image */}
              <div className="product-image">
                {product.imageUrl ? (
                  <img src={product.imageUrl} alt={product.name} />
                ) : (
                  <div className="image-placeholder">
                    <FaImage />
                  </div>
                )}
                <div className="discount-badge">{product.discount}% OFF</div>
                {product.label && (
                  <div className="label-badge">
                    <FaTag /> {product.label}
                  </div>
                )}
                {!product.active && <div className="inactive-overlay">Inactive</div>}
              </div>

              {/* Info */}
              <div className="product-info">
                <h3>{product.name}</h3>
                <p className="description">{product.description}</p>

                <div className="price-row">
                  <span className="offer-price">₹{product.offerPrice?.toLocaleString()}</span>
                  <span className="original-price">₹{product.originalPrice?.toLocaleString()}</span>
                </div>

                <div className="details-row">
                  <span className="stock">Stock: {product.stock || 0}</span>
                </div>
              </div>

              {/* Actions */}
              <div className="product-actions">
                <button
                  className={`btn-toggle ${product.active ? 'active' : 'inactive'}`}
                  onClick={() => toggleStatus(product)}
                >
                  {product.active ? <FaToggleOn /> : <FaToggleOff />}
                </button>
                <button className="btn-edit" onClick={() => handleEdit(product)}>
                  <FaEdit />
                </button>
                <button className="btn-delete" onClick={() => handleDelete(product.id)}>
                  <FaTrash />
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={resetForm}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingProduct ? 'Edit Offer Product' : 'Add Offer Product'}</h2>
              <button className="btn-close" onClick={resetForm}>×</button>
            </div>

            <form onSubmit={handleSubmit} className="modal-body">
              {/* Image Upload */}
              <div className="form-group">
                <label>Product Image</label>
                <div className="image-upload-area">
                  {imagePreview ? (
                    <div className="image-preview">
                      <img src={imagePreview} alt="Preview" />
                      <button
                        type="button"
                        className="remove-image"
                        onClick={() => {
                          setImageFile(null);
                          setImagePreview('');
                          setFormData({ ...formData, imageUrl: '' });
                        }}
                      >
                        <FaTimes />
                      </button>
                    </div>
                  ) : (
                    <label className="upload-label">
                      <FaUpload />
                      <span>Upload Image</span>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageChange}
                        style={{ display: 'none' }}
                      />
                    </label>
                  )}
                </div>
                <small>Image will be automatically resized to 800x800px</small>
              </div>

              <div className="form-group">
                <label>Product Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  placeholder="e.g., Summer Special Pack"
                />
              </div>

              <div className="form-group">
                <label>Description *</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  required
                  placeholder="Describe the product..."
                  rows="2"
                />
              </div>

              <div className="form-group">
                <label>Label/Category</label>
                <select
                  value={formData.label}
                  onChange={(e) => setFormData({ ...formData, label: e.target.value })}
                >
                  <option value="">Select Label</option>
                  {labels.map(label => (
                    <option key={label} value={label}>{label}</option>
                  ))}
                </select>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Original Price *</label>
                  <input
                    type="number"
                    value={formData.originalPrice}
                    onChange={(e) => setFormData({ ...formData, originalPrice: e.target.value })}
                    required
                    min="0"
                    placeholder="999"
                  />
                </div>

                <div className="form-group">
                  <label>Offer Price *</label>
                  <input
                    type="number"
                    value={formData.offerPrice}
                    onChange={(e) => setFormData({ ...formData, offerPrice: e.target.value })}
                    required
                    min="0"
                    placeholder="699"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Stock Quantity</label>
                  <input
                    type="number"
                    value={formData.stock}
                    onChange={(e) => setFormData({ ...formData, stock: e.target.value })}
                    min="0"
                    placeholder="100"
                  />
                </div>

                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={formData.active}
                      onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                    />
                    Active
                  </label>
                </div>
              </div>

              <div className="modal-footer">
                <button type="button" className="btn-cancel" onClick={resetForm}>
                  Cancel
                </button>
                <button type="submit" className="btn-submit" disabled={uploading}>
                  {uploading ? 'Uploading...' : editingProduct ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <style jsx>{`
        .offer-products-container {
          padding: 4px;
          background: var(--mobile-bg-page);
          min-height: 100vh;
        }

        .loading-state {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          min-height: 60vh;
          gap: 12px;
        }

        .spinner {
          width: 36px;
          height: 36px;
          border: 3px solid var(--mobile-border-light);
          border-top-color: var(--mobile-primary);
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        /* Header */
        .header {
          background: var(--mobile-bg-primary);
          padding: 14px;
          border-radius: 10px;
          margin-bottom: 12px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 10px;
        }

        .header-content {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 10px;
          flex: none;
          color: var(--mobile-text-primary);
        }

        .header-icon {
          font-size: 24px;
          color: var(--mobile-text-primary);
        }

        .header h1 {
          margin: 0;
          font-size: 18px;
          font-weight: 700;
        }

        .header p {
          margin: 2px 0 0 0;
          font-size: 12px;
          opacity: 0.9;
        }

        .btn-add {
          background: var(--mobile-bg-primary-card);
          color: white;
          border: none;
          padding: 8px 14px;
          border-radius: 8px;
          font-size: 13px;
          font-weight: 600;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 6px;
          transition: all 0.2s;
          white-space: nowrap;
        }

        .btn-add:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(243, 111, 33, 0.3);
        }

        /* Search */
        .search-section {
          margin-bottom: 12px;
        }

        .search-box {
          display: flex;
          align-items: center;
          gap: 8px;
          background: white;
          padding: 10px 12px;
          border-radius: 8px;
          border: 2px solid var(--mobile-border-light);
        }

        .search-box input {
          flex: 1;
          border: none;
          outline: none;
          font-size: 14px;
        }

        .clear-btn {
          cursor: pointer;
          color: #999;
          font-size: 14px;
        }

        /* Products Grid */
        .products-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
          gap: 10px;
        }

        .product-card {
          background: white;
          border-radius: 10px;
          overflow: hidden;
          box-shadow: 0 2px 6px rgba(0,0,0,0.08);
          border: 2px solid var(--mobile-border-light);
          transition: all 0.2s;
        }

        .product-card.inactive {
          opacity: 0.7;
        }

        .product-card:hover {
          transform: translateY(-3px);
          box-shadow: 0 6px 16px rgba(0,0,0,0.12);
          border-color: var(--mobile-bg-primary-card);
        }

        /* Image */
        .product-image {
          position: relative;
          width: 100%;
          height: 140px;
          background: #f5f5f5;
        }

        .product-image img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .image-placeholder {
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 40px;
          color: #ddd;
        }

        .discount-badge {
          position: absolute;
          top: 6px;
          right: 6px;
          background: var(--mobile-bg-primary-card);
          color: white;
          padding: 4px 10px;
          border-radius: 16px;
          font-size: 11px;
          font-weight: 700;
          box-shadow: 0 2px 6px rgba(0,0,0,0.2);
        }

        .label-badge {
          position: absolute;
          top: 6px;
          left: 6px;
          background: #000000;
          color: white;
          padding: 4px 8px;
          border-radius: 12px;
          font-size: 10px;
          font-weight: 600;
          display: flex;
          align-items: center;
          gap: 3px;
        }

        .inactive-overlay {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0,0,0,0.6);
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-weight: 600;
          font-size: 14px;
        }

        /* Info */
        .product-info {
          padding: 10px;
        }

        .product-info h3 {
          margin: 0 0 4px 0;
          font-size: 14px;
          font-weight: 600;
          color: var(--mobile-text-primary-card);
          line-height: 1.3;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
          min-height: 36px;
        }

        .description {
          margin: 0 0 8px 0;
          font-size: 11px;
          color: #666;
          line-height: 1.3;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
          min-height: 28px;
        }

        .price-row {
          display: flex;
          align-items: center;
          gap: 6px;
          margin-bottom: 6px;
        }

        .offer-price {
          font-size: 16px;
          font-weight: 700;
          color: var(--mobile-success);
        }

        .original-price {
          font-size: 12px;
          color: #999;
          text-decoration: line-through;
        }

        .details-row {
          display: flex;
          justify-content: space-between;
          font-size: 10px;
          color: #666;
          padding-top: 6px;
          border-top: 1px solid var(--mobile-border-light);
        }

        .stock {
          font-weight: 600;
        }

        /* Actions */
        .product-actions {
          display: flex;
          gap: 4px;
          padding: 8px;
          border-top: 1px solid var(--mobile-border-light);
        }

        .btn-toggle {
          flex: 1;
          border: none;
          padding: 6px;
          border-radius: 6px;
          font-size: 16px;
          cursor: pointer;
          transition: all 0.2s;
        }

        .btn-toggle.active {
          background: #d1fae5;
          color: #065f46;
        }

        .btn-toggle.inactive {
          background: #fee2e2;
          color: #991b1b;
        }

        .btn-edit {
          background: var(--mobile-primary);
          color: white;
          border: none;
          padding: 6px 10px;
          border-radius: 6px;
          font-size: 12px;
          cursor: pointer;
        }

        .btn-delete {
          background: var(--mobile-error);
          color: white;
          border: none;
          padding: 6px 10px;
          border-radius: 6px;
          font-size: 12px;
          cursor: pointer;
        }

        /* Empty State */
        .empty-state {
          grid-column: 1 / -1;
          text-align: center;
          padding: 48px 20px;
          background: white;
          border-radius: 10px;
        }

        .empty-icon {
          font-size: 56px;
          color: var(--mobile-border-medium);
          margin-bottom: 12px;
        }

        .empty-state p {
          margin: 0 0 16px 0;
          font-size: 16px;
          font-weight: 600;
          color: var(--mobile-text-primary-card);
        }

        /* Modal */
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0,0,0,0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          padding: 16px;
        }

        .modal-content {
          background: white;
          border-radius: 16px;
          max-width: 500px;
          width: 100%;
          max-height: 92vh;
          overflow-y: auto;
          box-shadow: 0 20px 60px rgba(0,0,0,0.3);
        }

        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 20px 24px;
          border-bottom: 1px solid #e5e7eb;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        }

        .modal-header h2 {
          margin: 0;
          font-size: 20px;
          font-weight: 700;
          color: white;
        }

        .btn-close {
          background: rgba(255,255,255,0.2);
          border: none;
          font-size: 24px;
          cursor: pointer;
          color: white;
          line-height: 1;
          width: 32px;
          height: 32px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s;
        }

        .btn-close:hover {
          background: rgba(255,255,255,0.3);
        }

        .modal-body {
          padding: 24px;
          background: #f8fafc;
        }

        .form-group {
          margin-bottom: 16px;
        }

        .form-group label {
          display: block;
          font-size: 12px;
          font-weight: 700;
          color: #374151;
          margin-bottom: 8px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .form-group input,
        .form-group textarea,
        .form-group select {
          width: 100%;
          padding: 12px 14px;
          border: 1px solid #d1d5db;
          border-radius: 10px;
          font-size: 14px;
          transition: all 0.2s;
          box-sizing: border-box;
          background: white;
          font-family: inherit;
        }

        .form-group input:focus,
        .form-group textarea:focus,
        .form-group select:focus {
          outline: none;
          border-color: #667eea;
          box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
        }

        .form-group small {
          font-size: 11px;
          color: #666;
          margin-top: 4px;
          display: block;
        }

        /* Image Upload */
        .image-upload-area {
          margin-bottom: 8px;
        }

        .upload-label {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 32px;
          border: 2px dashed #cbd5e1;
          border-radius: 12px;
          cursor: pointer;
          transition: all 0.3s;
          background: white;
        }

        .upload-label:hover {
          border-color: #667eea;
          background: #f8fafc;
          transform: translateY(-2px);
        }

        .upload-label svg {
          font-size: 40px;
          color: #667eea;
          margin-bottom: 12px;
        }

        .upload-label span {
          font-size: 14px;
          font-weight: 600;
          color: #374151;
        }

        .image-preview {
          position: relative;
          width: 100%;
          height: 220px;
          border-radius: 12px;
          overflow: hidden;
          box-shadow: 0 4px 12px rgba(0,0,0,0.1);
        }

        .image-preview img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .remove-image {
          position: absolute;
          top: 8px;
          right: 8px;
          background: rgba(0,0,0,0.7);
          color: white;
          border: none;
          width: 32px;
          height: 32px;
          border-radius: 50%;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .form-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 16px;
        }

        .checkbox-label {
          display: flex;
          align-items: center;
          gap: 8px;
          cursor: pointer;
          padding-top: 8px;
        }

        .checkbox-label input {
          width: 18px;
          height: 18px;
          cursor: pointer;
        }

        .modal-footer {
          display: flex;
          justify-content: flex-end;
          gap: 12px;
          padding: 20px 24px;
          border-top: 1px solid #e5e7eb;
          background: white;
        }

        .btn-cancel {
          background: #f3f4f6;
          color: #374151;
          border: none;
          padding: 12px 24px;
          border-radius: 10px;
          font-weight: 600;
          cursor: pointer;
          font-size: 14px;
          transition: all 0.2s;
        }

        .btn-cancel:hover {
          background: #e5e7eb;
        }

        .btn-submit {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          border: none;
          padding: 12px 28px;
          border-radius: 10px;
          font-weight: 600;
          cursor: pointer;
          font-size: 14px;
          transition: all 0.2s;
          box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
        }

        .btn-submit:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 16px rgba(102, 126, 234, 0.4);
        }

        .btn-submit:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        /* Responsive */
        @media (max-width: 768px) {
          .products-grid {
            grid-template-columns: repeat(2, 1fr);
          }

          .form-row {
            grid-template-columns: 1fr;
          }
        }

        @media (max-width: 480px) {
          .header {
            flex-direction: column;
            align-items: stretch;
          }

          .btn-add {
            justify-content: center;
          }
        }
      `}</style>
    </div>
  );
};

export default OfferProducts;
